import {
  useCallback, useEffect, useMemo, useState, useTransition,
} from 'react';
import {
  type AgsReportDataResponse, getAgsReportData,
} from '~/exportReport/exportReport.repository';
import {
  getReportDemographicDataFromResults,
  getReportDemographicLabelFromResults, type ReportDemographicData,
} from '~/exportReport/exportReportData.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';

type UseExportReportDataParams = {
  isPreviewMode?: boolean;
  template: ReportTemplate | null;
};

export const useExportReportData = ({ isPreviewMode = false, template }: UseExportReportDataParams) => {
  const [data, setData] = useState<AgsReportDataResponse | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, startTransition] = useTransition();
  const exportReportState = useToolsStateExportReportSelector();

  const spreadsheetData = useLatLngSpreadsheetData();

  const center = useMemo(() => {
    if (!exportReportState.isActive || exportReportState.params.type !== ExportReportType.Location) {
      return null;
    }

    const rowData = spreadsheetData.getRow(exportReportState.params.data.markerId);

    return rowData ?? null;
  }, [exportReportState, spreadsheetData]);

  const getDemographicData = useCallback((columnId: string, demographicId: string): ReportDemographicData | null => {
    if (isPreviewMode) {
      return null;
    }

    return getReportDemographicDataFromResults(data, columnId, demographicId);
  }, [data, isPreviewMode]);

  const getDemographicLabel = useCallback((demographicId: string): string | null => {
    return getReportDemographicLabelFromResults(data, demographicId);
  }, [data]);

  useEffect(() => {
    setIsError(false);

    if (!template || !center || isPreviewMode) {
      return;
    }

    startTransition(async () => {
      try {
        const results = await getAgsReportData({
          country: 'usa',
          varlistId: template.varlistId,
          lat: center.lat,
          lng: center.lng,
          radiiInMiles: [5, 10, 15],
        });

        setData(results.report);
      }
      catch {
        setIsError(true);
      }
    });
  }, [center, isPreviewMode, template]);

  useEffect(() => {
    if (!data) {
      return;
    }

    Object.assign(window, {
      isReportFullyLoaded: true,
    });
  }, [data]);

  return {
    isLoading,
    isError,
    getDemographicData,
    getDemographicLabel,
  };
};
